'use client';

import { useCallback } from 'react';

import { Menu } from '@base-ui/react/menu';
import Link from 'next/link';

import { SIGN_IN_HREF } from './nav-data';

import { trackButtonClick } from '@/lib/analytics/track';
import { useHasActiveBuyerOffers } from '@/lib/auth/buyer-offers';
import { useLeads } from '@/lib/auth/leads';
import { useSessionExtras } from '@/lib/auth/session-extras';
import { initialsFor, signOut, useUserSession } from '@/lib/auth/user-session';
import { opendoorUrl } from '@/lib/urls';

const ACCOUNT_HREF = opendoorUrl('/account');
const CLOSING_DASHBOARD_HREF = opendoorUrl('/closing-dashboard');
const DASHBOARD_HREF = opendoorUrl('/dashboard');
const PROPERTIES_HREF = opendoorUrl('/properties');
const HELP_CENTER_HREF = 'https://help.opendoor.com';
const MAX_LEADS_IN_MENU = 3;

const signInLinkClass =
  'rounded-full px-4 py-2 text-base font-medium text-ink transition hover:bg-state-hover';

const menuItemClass =
  'flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-ink outline-none transition data-[highlighted]:bg-state-hover text-body-md';

function Divider() {
  return <div className="my-1 h-px bg-line-soft" />;
}

// Reserves the same 20px gutter the icon-bearing rows (Dashboard, Sign out)
// consume, so labels in iconless rows line up with labels in icon rows under
// the menu's `gap-3` flex layout.
function IconSlot() {
  return <span aria-hidden className="inline-block h-5 w-5 shrink-0" />;
}

function HomeIcon() {
  return (
    <svg
      aria-hidden
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10a1 1 0 0 0 1 1h4v-7h4v7h4a1 1 0 0 0 1-1V10" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      aria-hidden
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-2" />
      <path d="M9 12h12" />
      <path d="M18 9l3 3-3 3" />
    </svg>
  );
}

export function HeaderAccount() {
  const auth = useUserSession();
  const leads = useLeads();
  const extras = useSessionExtras();
  const hasActiveBuyerOffers = useHasActiveBuyerOffers();

  const handleAvatarClick = useCallback(() => {
    trackButtonClick('user-account-menu-click');
  }, []);
  const handleSignInClick = useCallback(() => {
    trackButtonClick('account-section-click');
  }, []);
  const handleLeadClick = useCallback(() => {
    trackButtonClick('account-lead-click');
  }, []);
  const handleMoreClick = useCallback(() => {
    trackButtonClick('account-leads-more');
  }, []);
  const handleClosingDashClick = useCallback(() => {
    trackButtonClick('account-closing-dashboard');
  }, []);
  const handleDashboardClick = useCallback(() => {
    trackButtonClick('dashboard-link');
  }, []);
  const handleAccountClick = useCallback(() => {
    trackButtonClick('account-link');
  }, []);
  const handleHelpCenterClick = useCallback(() => {
    trackButtonClick('account-help-center');
  }, []);
  const handleSignOut = useCallback(() => {
    trackButtonClick('account-signout');
    void signOut();
  }, []);

  if (auth.kind !== 'authenticated') {
    return (
      <Link
        href={SIGN_IN_HREF}
        prefetch={false}
        className={signInLinkClass}
        onClick={handleSignInClick}
      >
        Sign in
      </Link>
    );
  }

  const initials = initialsFor(auth.human);
  const visibleLeads = leads.slice(0, MAX_LEADS_IN_MENU);
  const hasLeadsOverflow = leads.length > MAX_LEADS_IN_MENU;
  const hasLeadsSection = visibleLeads.length > 0;

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label="Your account menu"
        onClick={handleAvatarClick}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-ink text-canvas outline-none transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
      >
        <span aria-hidden className="text-xs font-medium">
          {initials}
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={8} align="end" className="z-50">
          <Menu.Popup className="min-w-[260px] rounded-2xl border border-line-soft bg-canvas p-2 shadow-lg">
            {visibleLeads.map((lead) => (
              <Menu.Item
                key={lead.id}
                className={menuItemClass}
                render={<Link href={lead.url} prefetch={false} onClick={handleLeadClick} />}
              >
                <IconSlot />
                <span>
                  {lead.street ?? 'Your property'}
                  {lead.readableOfferState ? ` (${lead.readableOfferState})` : ''}
                </span>
              </Menu.Item>
            ))}
            {hasLeadsOverflow && (
              <Menu.Item
                className={menuItemClass}
                render={<Link href={PROPERTIES_HREF} prefetch={false} onClick={handleMoreClick} />}
              >
                <IconSlot />
                <span>More</span>
              </Menu.Item>
            )}
            {hasLeadsSection && <Divider />}
            {extras.hasResaleClosingDashboard && (
              <Menu.Item
                className={menuItemClass}
                render={
                  <Link
                    href={CLOSING_DASHBOARD_HREF}
                    prefetch={false}
                    onClick={handleClosingDashClick}
                  />
                }
              >
                <IconSlot />
                <span>Closing dash</span>
              </Menu.Item>
            )}
            {extras.hasResaleClosingDashboard && <Divider />}
            {hasActiveBuyerOffers && (
              <Menu.Item
                className={menuItemClass}
                render={
                  <Link href={DASHBOARD_HREF} prefetch={false} onClick={handleDashboardClick} />
                }
              >
                <HomeIcon />
                <span>Dashboard</span>
              </Menu.Item>
            )}
            <Menu.Item
              className={menuItemClass}
              render={<Link href={ACCOUNT_HREF} prefetch={false} onClick={handleAccountClick} />}
            >
              <IconSlot />
              <span>Account</span>
            </Menu.Item>
            <Divider />
            <Menu.Item
              className={menuItemClass}
              render={
                <Link href={HELP_CENTER_HREF} prefetch={false} onClick={handleHelpCenterClick} />
              }
            >
              <IconSlot />
              <span>Help center</span>
            </Menu.Item>
            <Menu.Item className={menuItemClass} onClick={handleSignOut}>
              <LogoutIcon />
              <span>Sign out</span>
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
